import React, { useState, useRef } from 'react';

import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { Box } from '@mui/material';

import { useTranslation } from 'next-i18next';

import { GroupChannel } from '@sendbird/chat/groupChannel';

import Button from 'src/components/ui/Button';
import type { icons } from 'src/components/ui/Icon';
import Icon from 'src/components/ui/Icon';

import IconButton from 'src/components/ui/IconButton';
import { ExternalShareModal } from 'src/components/ad/ExternalShareModal';
import InvitationRequestSuccessModal from 'src/components/company/InvitationRequestSuccessModal';
import ConfirmModal from 'src/components/ui/ConfirmModal';
import Typography from 'src/components/ui/Typography';
import ReportModal from 'src/components/ui/ReportModal';

import useAlerts from 'src/hooks/useAlerts';
import useUser from 'src/hooks/useUser';
import useModalOpenState, { useShareAsPrivateMessageModal, useShareViaModal } from 'src/hooks/useModalOpenState';

import {
    useCreateInvitationMutation,
    useDeleteInvitationMutation,
    useUpdateInvitationMutation,
    useRequestAdminAccessMutation,
    useRemoveCompanyMutation,
    useCreateGroupInvitationMutation,
    useDeleteGroupInvitationMutation,
    useUpdateGroupInvitationMutation,
    useRemoveGroupMembershipMutation,
    useAddContentEventMutation,
} from 'src/hooks/__generated__/queries';
import type { CompanyProfileQuery, Medium } from 'src/types/__generated__/graphql';
import { InvitationStatus } from 'src/types/__generated__/graphql';
import useAppRouter from 'src/hooks/useAppRouter';
import { routePathContactsWithChannelUrl, routePathContactsWithUserId } from 'src/constants/router';
import SearchAllUsersAndGroupsModal from 'src/components/ui/SearchAllUsersAndGroupsModal';
import RenderLineBreaks from 'src/components/ui/RenderLineBreaks';
import { getAbsoluteUrl } from 'src/utils/formatUrl';
import { useCompanyActivityToDisplay } from 'src/hooks/useActivityToDisplay';
import TinyPreviewCard from 'src/components/ui/TinyPreviewCard/TinyPreviewCard';
import { styledComponent } from 'src/utils/styled';
import type { MenuOption } from 'src/types/MenuOption';
import { AnalyticsEvent } from 'src/utils/analytics';
import useDynamicLogEvents from 'src/hooks/useDynamicLogEvents';

type Props = {
    company: CompanyProfileQuery['companyProfile'];
};

const StyledButton = styledComponent(Button)`
    $ > span {
        min-width: 0;
    }
`;

const CompanyProfilePlusButton: React.FC<Props> = ({ company }) => {
    const { t } = useTranslation(['profile', 'common', 'company']);
    const { addAlert } = useAlerts();
    const router = useAppRouter();
    const getCompanyActivityToDisplay = useCompanyActivityToDisplay();
    const currentUser = useUser();
    const menuAnchor = useRef<HTMLDivElement | null>(null);
    const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
    const [createInvitation] = useCreateInvitationMutation();
    const [deleteInvitation] = useDeleteInvitationMutation();
    const [updateInvitation] = useUpdateInvitationMutation();
    const [createGroupInvitation] = useCreateGroupInvitationMutation();
    const [deleteGroupInvitation] = useDeleteGroupInvitationMutation();
    const [updateGroupInvitation] = useUpdateGroupInvitationMutation();
    const [removeGroupMembership] = useRemoveGroupMembershipMutation();
    const [requestAdminAccess] = useRequestAdminAccessMutation();
    const [removeCompany] = useRemoveCompanyMutation();
    const [addContentEventMutation] = useAddContentEventMutation();
    const { logEventDynamic } = useDynamicLogEvents();

    const { props: invitationRequestSuccessModalProps, open: openInvitationRequestSuccessModal } = useModalOpenState();
    const { props: confirmModalProps, open: openConfirmModal } = useModalOpenState();
    const { props: shareModalProps, open: openShareModal } = useShareViaModal();
    const { props: searchContactModalProps, open: openSearchContactModal } = useShareAsPrivateMessageModal();
    const { props: reportModalProps, open: openReportModal } = useModalOpenState();

    const handleOpenMenu = () => {
        if (menuAnchor.current) {
            setMenuAnchorEl(menuAnchor.current);
        }
    };
    const handleCloseMenu = () => {
        setMenuAnchorEl(null);
    };
    const handleRequestAdmin = async () => {
        logEventDynamic(AnalyticsEvent.RequestAdminAccess);
        try {
            await requestAdminAccess({
                variables: { companyId: company.id },
                refetchQueries: ['CompanyProfile', 'CurrentUser'],
                awaitRefetchQueries: true,
            });
            addAlert({
                type: 'success',
                message: t('company:requestAdminSuccess'),
            });
        } catch (error: unknown) {
            addAlert({
                type: 'error',
                message: (error as Error).message,
            });
        }
    };

    const manageCollaboratorInvitation =
        ({
            status,
            createOrDelete,
            isCompanyToGroupAffilitation,
        }: {
            status?: InvitationStatus;
            createOrDelete?: 'create' | 'delete';
            isCompanyToGroupAffilitation: boolean;
        }) =>
        async (ev: React.MouseEvent) => {
            ev.stopPropagation();
            try {
                if (createOrDelete === 'create') {
                    if (isCompanyToGroupAffilitation) {
                        await createGroupInvitation({
                            variables: { groupId: company.id },
                            awaitRefetchQueries: true,
                            refetchQueries: ['CompanyProfile', 'CurrentUser'],
                        });
                    } else {
                        await createInvitation({
                            variables: { companyId: company.id },
                            awaitRefetchQueries: true,
                            refetchQueries: ['CompanyProfile', 'CurrentUser'],
                        });
                    }
                    openInvitationRequestSuccessModal();
                } else if (status) {
                    let successAlertTranslationKey;
                    let successAlertParams = {};
                    if (isCompanyToGroupAffilitation) {
                        await updateGroupInvitation({
                            variables: { groupId: company.id, status },
                            refetchQueries: ['CompanyProfile', 'CurrentUser'],
                            awaitRefetchQueries: true,
                        });
                        successAlertTranslationKey = `notifications:${
                            status === InvitationStatus.Accepted
                                ? 'groupInvitationSuccessWithName'
                                : 'invitationIgnoredSuccess'
                        }`;
                        successAlertParams = {
                            companyName: currentUser?.affiliatedCompany?.name,
                            groupName: company.name,
                        };
                    } else {
                        await updateInvitation({
                            variables: { companyId: company.id, status },
                            refetchQueries: ['CompanyProfile', 'CurrentUser'],
                            awaitRefetchQueries: true,
                        });
                        successAlertTranslationKey = `notifications:${
                            status === InvitationStatus.Accepted ? 'collaborationSuccess' : 'collaborationIgnoreSuccess'
                        }`;
                        successAlertParams = {
                            name: company.name,
                        };
                    }
                    addAlert({
                        type: 'success',
                        message: t(successAlertTranslationKey, successAlertParams),
                    });
                } else if (createOrDelete === 'delete') {
                    if (isCompanyToGroupAffilitation) {
                        await deleteGroupInvitation({
                            variables: { groupId: company.id },
                            refetchQueries: ['CompanyProfile', 'CurrentUser'],
                            awaitRefetchQueries: true,
                        });
                    } else {
                        await deleteInvitation({
                            variables: { companyId: company.id },
                            refetchQueries: ['CompanyProfile', 'CurrentUser'],
                            awaitRefetchQueries: true,
                        });
                    }
                    addAlert({
                        type: 'success',
                        message: t('edit-profile:deleteInvitationSuccess'),
                    });
                }
            } catch (error: unknown) {
                addAlert({
                    type: 'error',
                    message: (error as Error).message,
                });
            }
        };

    const handleRemoveCollaboration = async (isCompanyToGroupAffilitation: boolean) => {
        if (isCompanyToGroupAffilitation) {
            await removeGroupMembership({
                variables: { groupId: company.id },
                refetchQueries: ['CompanyProfile', 'CurrentUser'],
                awaitRefetchQueries: true,
            });
            addAlert({
                type: 'success',
                message: t('company:removeGroupMembershipFromCompany', {
                    companyName: currentUser?.affiliatedCompany?.name,
                    groupName: company.name,
                }),
            });
        } else {
            await removeCompany({
                variables: { companyId: company.id },
                refetchQueries: ['CompanyProfile', 'CurrentUser'],
                awaitRefetchQueries: true,
            });
            addAlert({
                type: 'success',
                message: t('company:removeMembershipFromUser'),
            });
        }
    };

    const displayAffiliationButton = company.currentUserIsAbleToJoin;
    const displayGroupAffiliationButton = company.currentUserAffiliatedCompanyIsAbleToJoin;
    const invitationStatus = company.groupInvitationStatus ?? company.invitationStatus;
    let affiliationOption;
    // need only one affiliation option as current user is
    // either non affiliated and can affiliate himself
    // or can be admin of his company and can affiliate his company
    switch (invitationStatus) {
        case InvitationStatus.Uninvited:
            affiliationOption = {
                mainTranslationKey: displayGroupAffiliationButton
                    ? 'company:affiliateMyCompanyToGroup'
                    : 'company:joinTeam',
                condition: Boolean(displayGroupAffiliationButton) || Boolean(displayAffiliationButton),
                iconName: displayGroupAffiliationButton ? 'Collaborators' : ('AddUser' as keyof typeof icons),
                handleClick: manageCollaboratorInvitation({
                    createOrDelete: 'create',
                    isCompanyToGroupAffilitation: Boolean(displayGroupAffiliationButton),
                }),
            };
            break;
        case InvitationStatus.ToRespond:
            affiliationOption = {
                mainTranslationKey: displayGroupAffiliationButton
                    ? 'company:affiliateMyCompanyToGroup'
                    : 'company:joinTeam',
                subTranslationKey: 'company:acceptRequestQuestion',
                condition: Boolean(displayGroupAffiliationButton) || Boolean(displayAffiliationButton),
                iconName: displayGroupAffiliationButton ? 'Collaborators' : ('AddUser' as keyof typeof icons),
                rightContent: (
                    <Box display="flex" alignItems="center">
                        <Box ml="1rem" mr="0.625rem">
                            <IconButton
                                color="white"
                                style={{ padding: '0' }}
                                onClick={manageCollaboratorInvitation({
                                    status: InvitationStatus.Refused,
                                    isCompanyToGroupAffilitation: Boolean(displayGroupAffiliationButton),
                                })}
                                iconProps={{
                                    name: 'CloseCircle',
                                    size: '1.5rem',
                                }}
                            />
                        </Box>
                        <IconButton
                            color="white"
                            style={{ padding: '0' }}
                            onClick={manageCollaboratorInvitation({
                                status: InvitationStatus.Accepted,
                                isCompanyToGroupAffilitation: Boolean(displayGroupAffiliationButton),
                            })}
                            iconProps={{
                                name: 'CheckMarkCircleThin',
                                size: '1.35rem',
                            }}
                        />
                    </Box>
                ),
            };
            break;
        case InvitationStatus.Pending:
            affiliationOption = {
                mainTranslationKey: displayGroupAffiliationButton
                    ? 'company:affiliateMyCompanyToGroup'
                    : 'company:joinTeam',
                subTranslationKey: 'company:waitingRequestAnswer',
                condition: Boolean(displayGroupAffiliationButton) || Boolean(displayAffiliationButton),
                iconName: displayGroupAffiliationButton ? 'Collaborators' : ('AddUser' as keyof typeof icons),
                rightContent: (
                    <Box display="flex" alignItems="center">
                        <Box ml="1rem" mr="0.5rem">
                            <IconButton
                                color="white"
                                style={{ padding: '0' }}
                                iconProps={{
                                    name: 'Time',
                                    size: '1.5rem',
                                }}
                                disabled
                            />
                        </Box>
                        <IconButton
                            color="white"
                            style={{ padding: '0' }}
                            onClick={manageCollaboratorInvitation({
                                createOrDelete: 'delete',
                                isCompanyToGroupAffilitation: Boolean(displayGroupAffiliationButton),
                            })}
                            iconProps={{
                                name: 'CloseCircle',
                                size: '1.5rem',
                            }}
                        />
                    </Box>
                ),
            };
            break;
        default:
            break;
    }

    const currentUserAffiliatedCompanyIsGroupMember = currentUser?.affiliatedCompany?.group?.id === company.id;
    const currentUserIsAffiliatedToCompany = currentUser?.affiliatedCompany?.id === company.id;

    const optionDisplayConditions = {
        anyone: true,
        isLoggedIn: currentUser,
        isPro: currentUser?.isProfessionnal,
        canJoinThisCompany: company.currentUserIsAbleToJoin,
        canContact: company.contactUrl,
        hasRequestedToJoinThisCompany: company.groupInvitationStatus ?? company.invitationStatus,
        hasJoinedThisCompany: currentUser?.affiliatedCompany?.id === company.id,
        isAdminOfThisCompany:
            currentUser?.affiliatedCompany?.id === company.id && currentUser.isAdminOfAffiliatedCompany,
    };
    const moreOptions: Record<string, unknown> & Record<string, MenuOption | undefined> = {
        shareInPrivateMessage: {
            condition: Boolean(optionDisplayConditions.isLoggedIn),
            iconName: 'ChatPrimaryInactive' as keyof typeof icons,
            handleClick: openSearchContactModal,
            mainTranslationKey: 'common:shareInPrivateMessage',
        },
        shareVia: {
            iconName: 'ExternalLink' as keyof typeof icons,
            handleClick: openShareModal,
            condition: true,
            mainTranslationKey: 'common:shareVia',
        },
        report: {
            iconName: 'Alert' as keyof typeof icons,
            mainTranslationKey: 'common:report.reportCompany',
            condition: Boolean(currentUser),
            handleClick: openReportModal,
        },
        affiliationOption,
        requestInfo: {
            mainTranslationKey: 'company:joinUsOrInformationRequest',
            condition: Boolean(optionDisplayConditions.canContact),
            iconName: 'Info' as keyof typeof icons,
            handleClick(): void {
                if (typeof company.contactUrl === 'string') {
                    window.open(getAbsoluteUrl(company.contactUrl), '_blank', 'noopener');
                }
            },
        },
        requestAdminRights: {
            mainTranslationKey: 'company:requestAdmin',
            condition: optionDisplayConditions.hasJoinedThisCompany && !optionDisplayConditions.isAdminOfThisCompany,
            iconName: 'Filter' as keyof typeof icons,
            handleClick: handleRequestAdmin,
        },
        leave: {
            mainTranslationKey: currentUserAffiliatedCompanyIsGroupMember
                ? 'company:removeMyCompanyFromGroup'
                : 'company:leaveTeam',
            condition: Boolean(currentUserAffiliatedCompanyIsGroupMember || currentUserIsAffiliatedToCompany),
            iconName: currentUserAffiliatedCompanyIsGroupMember ? 'RemoveGroup' : ('RemoveUser' as keyof typeof icons),
            handleClick(): void {
                logEventDynamic(AnalyticsEvent.LeaveTeam);
                if (currentUserAffiliatedCompanyIsGroupMember) {
                    openConfirmModal();
                } else {
                    // eslint-disable-next-line -- void
                    handleRemoveCollaboration(false);
                }
            },
        },
    };

    return (
        <>
            <div ref={menuAnchor}>
                <StyledButton
                    onClick={handleOpenMenu}
                    target="_blank"
                    size="medium"
                    data-target-id="companyProfilePlusButton-button"
                    variant="outlined"
                    sx={{
                        p: '0.5rem',
                        minWidth: 0,
                        ml: '0.5rem',
                        width: '2.5rem',
                        height: '2.5rem',
                    }}
                    shrink
                >
                    <Icon name="DotsHorizontalRounded" />
                </StyledButton>
            </div>

            <Menu
                anchorEl={menuAnchorEl}
                open={Boolean(menuAnchorEl)}
                onClose={handleCloseMenu}
                keepMounted
                disableRestoreFocus
                disableEnforceFocus
            >
                {Object.entries(moreOptions).map(
                    ([_key, option]) =>
                        option?.condition && (
                            <MenuItem
                                key={`menuItem-${option.mainTranslationKey}`}
                                onClick={option.handleClick}
                                sx={{ minHeight: 'auto' }}
                            >
                                <Box display="flex" alignItems="center">
                                    <Icon name={option.iconName} size="1.25rem" style={{ marginLeft: '-0.125rem' }} />
                                    <Box ml={1} display="flex" flexDirection="column">
                                        <RenderLineBreaks>
                                            <Typography color="primary" variant="h6">
                                                {t(option.mainTranslationKey)}
                                            </Typography>
                                        </RenderLineBreaks>

                                        {option.subTranslationKey && (
                                            <Typography variant="caption" style={{ lineHeight: '1' }}>
                                                {t(option.subTranslationKey)}
                                            </Typography>
                                        )}
                                    </Box>
                                    {option.rightContent ?? null}
                                </Box>
                            </MenuItem>
                        ),
                )}
            </Menu>

            {invitationRequestSuccessModalProps.open && (
                <InvitationRequestSuccessModal {...invitationRequestSuccessModalProps} />
            )}
            {confirmModalProps.open && (
                <ConfirmModal
                    title={t('company:disaffiliation')}
                    description={t('company:disaffiliationModalText', {
                        groupName: company.name,
                    })}
                    onClick={() => handleRemoveCollaboration(true)}
                    confirm={t('common:modal.confirm')}
                    {...confirmModalProps}
                />
            )}
            {shareModalProps.open && (
                <ExternalShareModal
                    {...shareModalProps}
                    externalShareTitle={company.name}
                    shareContentProps={{
                        type: 'company',
                        id: company.id,
                    }}
                />
            )}
            {searchContactModalProps.open && (
                <SearchAllUsersAndGroupsModal
                    title={t('common:shareInPrivateMessage')}
                    topArea={
                        <TinyPreviewCard
                            type="company"
                            title={company.name}
                            subtitle={t(getCompanyActivityToDisplay(company))}
                            media={company.image ? ([company.image] as Medium[]) : undefined}
                        />
                    }
                    submitLabel={t('ad:share')}
                    onSelection={async (selectedRecipient) => {
                        const contactRoute =
                            selectedRecipient instanceof GroupChannel
                                ? `${routePathContactsWithChannelUrl}${selectedRecipient.url}`
                                : `${routePathContactsWithUserId}${selectedRecipient.id}`;
                        await router.push(`${contactRoute}&sharedCompanyId=${company.id}`);
                        await addContentEventMutation({
                            variables: {
                                companyId: company.id,
                                eventType: 'shared_internally',
                            },
                            fetchPolicy: 'no-cache',
                        });
                    }}
                    renderGroupConversationButton
                    showConversations
                    {...searchContactModalProps}
                />
            )}
            {reportModalProps.open && (
                <ReportModal
                    companyId={company.id}
                    open={reportModalProps.open}
                    onClose={() => {
                        reportModalProps.onClose();
                    }}
                />
            )}
        </>
    );
};

export default CompanyProfilePlusButton;
